import { Box } from '@lifeomic/chroma-react/components/Box';
import { ButtonUnstyled } from '@lifeomic/chroma-react/components/ButtonUnstyled';
import { Divider } from '@lifeomic/chroma-react/components/Divider';
import { DotLoader } from '@lifeomic/chroma-react/components/DotLoader';
import { makeStyles } from '@lifeomic/chroma-react/styles';
import { ChevronRight } from '@lifeomic/chromicons';
import { lighten } from '@mui/material/styles';
import clsx from 'clsx';
import { Text } from '@lifeomic/chroma-react/components/Text';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { FixedSizeList } from 'react-window';
import { theme } from '../../../../theme/phc';
import { MedicalResultChartCondensed } from '../Measurements/Graphs/Ranges/MedicalResultsChartCondensed/MedicalResultChartCondensed';
import { NoDataGuard } from './NoDataGuard';
import { useFetchDimensionData } from './hooks/useFetchDimensionData';
import {
  MedicalResultHistory,
  useFetchMedicalResultHistories,
} from './hooks/useFetchMedicalResultHistories';
import { useSearchContext } from './providers/Search';
import { useSelectionContext } from './providers/Selection';
import { LabResultsConfigItem } from './useLabResultsConfig';
import { filterMedicalResultHistories } from './utils';

const ROW_HEIGHT = 185;

export const useStyles = makeStyles(() => ({
  titleContainer: {
    alignItems: 'center',
    backgroundColor: lighten(theme.palette.background.default, 0.5),
    color: theme.palette.text.hint,
    display: 'flex',
    fontSize: theme.pxToRem(12),
    fontWeight: theme.typography.fontWeightBold,
    gap: theme.spacing(0.75),
    letterSpacing: theme.pxToRem(1.2),
    padding: theme.spacing(1, 2),
    position: 'sticky',
    textTransform: 'uppercase',
    zIndex: 30,
    top: 0,
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: lighten(theme.palette.background.default, 0.3),
    },
  },
  titleContainerExpanded: {
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  row: {
    '&:not(:last-of-type)': {
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
  },
  btn: {
    position: 'relative',
    maxHeight: '100%',
  },
  sectionContainer: {
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  section: {
    '&:hover, &:focus': {
      transition: 'background 0.25s ease-out',
      '& $chevron': {
        transform: `translate3d(${theme.spacing(1)}, 0, 0)`,
        transition: 'transform 0.125s ease',
      },
    },
  },
  chevronContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: theme.spacing(12),
  },
  chevron: {
    color: theme.palette.black[500],
    transition: 'transform 0.125s ease',
    transform: 'rotate(0deg)',
  },
  chevronExpanded: {
    transform: 'rotate(90deg)',
  },
  graphContainer: {
    maxHeight: ROW_HEIGHT,
  },
  paddingTop: {
    paddingTop: theme.spacing(3),
  },
  extractedBadge: {
    backgroundColor: theme.palette.primary[50],
    borderRadius: theme.pxToRem(10),
    color: theme.palette.primary.main,
    fontSize: theme.pxToRem(10),
    fontWeight: theme.typography.fontWeightBold,
    letterSpacing: theme.pxToRem(0.8),
    padding: theme.spacing(0.25, 0.75),
  },
}));

type LabResultsItemRowProps = {
  medicalResultHistory: MedicalResultHistory;
  patientId?: string;
};

const LabResultsItemRow = React.memo(
  ({ medicalResultHistory, patientId }: LabResultsItemRowProps) => {
    const classes = useStyles();

    return (
      <NoDataGuard
        dimensionDatum={medicalResultHistory}
        results={medicalResultHistory.medicalResultHistory}
      >
        <Box
          className={classes.section}
          align="center"
          justify="space-between"
          fullWidth
        >
          <Box className={classes.graphContainer} fullWidth>
            <MedicalResultChartCondensed.WithPrefetchedData
              resultsHistory={medicalResultHistory}
              contained
              subjectId={patientId}
            />
          </Box>
          <Box className={classes.chevronContainer}>
            <ChevronRight className={classes.chevron} width={18} height={18} />
          </Box>
        </Box>
      </NoDataGuard>
    );
  },
);

const messages = defineMessages({
  noData: {
    id: 'labResults.item.noData',
    defaultMessage: 'No Results',
  },
  extractedBadge: {
    id: 'labResults.item.extractedBadge',
    defaultMessage: 'Extracted',
  },
});

type LabResultsItemProps = {
  patient: fhir.Patient;
  labResultsConfigItem: LabResultsConfigItem;
  onClick: (medicalResultHistory: MedicalResultHistory) => void;
};

export function LabResultsItem({
  patient,
  labResultsConfigItem,
  onClick,
}: LabResultsItemProps) {
  const classes = useStyles();
  const [isExpanded, setIsExpanded] = useState(false);
  const labResultSectionRef = useRef<HTMLDivElement>(null);
  const { debouncedSearchTerm, recency, annotatedOnly } = useSearchContext();
  const { selection } = useSelectionContext();

  const { formatMessage } = useIntl();

  const { data: dimensionData } = useFetchDimensionData({
    patientId: patient.id as string,
    labResultsConfigItem,
  });

  const { data: medicalResultHistories, isLoading } =
    useFetchMedicalResultHistories({
      patientId: patient.id as string,
      dimensionData,
      labResultsConfigItem,
    });

  const filteredMedicalResultHistories = useMemo(() => {
    if (medicalResultHistories === undefined) {
      return [];
    }
    return filterMedicalResultHistories(
      medicalResultHistories,
      debouncedSearchTerm,
      recency,
      annotatedOnly,
    );
  }, [medicalResultHistories, debouncedSearchTerm, recency, annotatedOnly]);

  const scrollToLabResultSection = useCallback(() => {
    if (labResultSectionRef.current) {
      labResultSectionRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  useEffect(() => {
    if (selection?.title === labResultsConfigItem.title) {
      scrollToLabResultSection();
    }
  }, [selection, scrollToLabResultSection, labResultsConfigItem.title]);

  return (
    <Box
      direction="column"
      className={classes.sectionContainer}
      fullWidth
      ref={labResultSectionRef}
    >
      <Box
        className={`${classes.titleContainer} ${
          isExpanded || debouncedSearchTerm
            ? classes.titleContainerExpanded
            : ''
        }`}
        fullWidth
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <ChevronRight
          className={`${classes.chevron} ${
            isExpanded ||
            // if the user has searched and there are results, show the chevron expanded
            (debouncedSearchTerm && filteredMedicalResultHistories.length > 0)
              ? classes.chevronExpanded
              : ''
          }`}
          width={18}
          height={18}
        />
        {labResultsConfigItem.title}
        {labResultsConfigItem.source === 'documentExtractionV2' ? (
          <Text className={classes.extractedBadge} size="caption">
            {formatMessage(messages.extractedBadge)}
          </Text>
        ) : null}
      </Box>
      {(isExpanded || debouncedSearchTerm) &&
        (isLoading ? (
          <Box fullWidth fullHeight justify="center" align="center" width={15}>
            <DotLoader size={0} />
          </Box>
        ) : filteredMedicalResultHistories.length === 0 && isExpanded ? (
          // if it's explicitly expanded and there are no results, show the no data message
          <Box padding={2} direction="column" fullWidth>
            <span>{formatMessage(messages.noData)}</span>
          </Box>
        ) : filteredMedicalResultHistories.length < 30 ? (
          // if there are fewer than 30 results, show them all for better scroll behavior
          filteredMedicalResultHistories.map((history, index) => (
            <>
              {index > 0 && <Divider spacing={3} />}
              <ButtonUnstyled
                className={clsx(classes.btn, {
                  [classes.paddingTop]: index === 0,
                })}
                key={history.dimension.id}
                onClick={() => onClick(history)}
                fullWidth
              >
                <LabResultsItemRow
                  medicalResultHistory={history}
                  patientId={patient.id}
                />
              </ButtonUnstyled>
            </>
          ))
        ) : (
          <FixedSizeList
            itemSize={ROW_HEIGHT}
            itemCount={filteredMedicalResultHistories.length}
            height={Math.min(
              filteredMedicalResultHistories.length * ROW_HEIGHT,
              500,
            )}
            width={'100%'}
            overscanCount={5} // render up to 5 extra items to avoid flickering
          >
            {({ index, style }) => (
              <div style={style} className={classes.row}>
                <ButtonUnstyled
                  className={classes.btn}
                  key={filteredMedicalResultHistories[index].dimension.id}
                  onClick={() => {
                    onClick(filteredMedicalResultHistories[index]);
                  }}
                  fullWidth
                >
                  <LabResultsItemRow
                    medicalResultHistory={filteredMedicalResultHistories[index]}
                    patientId={patient.id}
                  />
                </ButtonUnstyled>
              </div>
            )}
          </FixedSizeList>
        ))}
    </Box>
  );
}
